import { ApolloError } from 'apollo-client'
import {
  useGetLicenseRedemptionQuery,
  useGetWorkspaceCustomerQuery,
} from 'gqlTypes'

const ALLOWED_QUERY_LICENSE_STATUS = ['PENDING', 'REDEEMED']

type OnError = (message: string) => void

export const useLicenseRedemption = (token?: string, onError?: OnError) => {
  const handleOnError = ({ graphQLErrors }: ApolloError) => {
    const errorMessages = graphQLErrors.map(({ message, extensions }) => {
      const code = extensions?.code || 'none'

      if (code.match(/UNAUTHORIZED/gi)) {
        return 'Could not approve license request. Please contact the workspace’s admin for access to the workspace.'
      } else {
        return message
      }
    })

    if (errorMessages.length > 0) {
      const message = errorMessages.join(', ')
      onError?.(message)
    }
  }

  const licenseRedemption = useGetLicenseRedemptionQuery({
    variables: {
      /* Prevent the types from breaking. */
      token: token || '',
    },
    onCompleted: data => {
      const licenseStatus = data.licenseRedemption.status

      if (licenseStatus === 'CANCELED') {
        onError?.(
          'Redeem license key request has been cancelled by the workspace'
        )
      }
    },
    onError: handleOnError,
    skip: !token,
  })

  const license = licenseRedemption?.data?.licenseRedemption
  const customerId = ALLOWED_QUERY_LICENSE_STATUS.includes(
    license?.status || ''
  )
    ? license?.customer?.id
    : undefined

  const customer = useGetWorkspaceCustomerQuery({
    variables: { identifier: customerId || '' },
    onError: handleOnError,
    skip: !customerId,
  })

  /**
   * At this point, customers can only have one workspace. If, at some point,
   * customers can have more than 1 workspace, we will need to decide what
   * workspace should we pick here to render its information.
   */
  const workspace = customer.data?.workspaceCustomer?.workspaces?.entries[0]

  return {
    data: {
      licenseRedemption: licenseRedemption.data?.licenseRedemption,
      workspace,
    },
    loading: licenseRedemption.loading || customer.loading,
    error: licenseRedemption.error || customer.error,
  }
}
